import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

import GuideImage from "assets/img/guide.png";
import { useTranslation } from "react-i18next";

/**
 * Pantalla "Guía de Usuario".
 *
 * Objetivos:
 * - mantener una estética muy similar a la pantalla About
 * - servir como página de aterrizaje principal
 * - explicar qué hace la aplicación y cómo utilizarla
 * - soportar textos largos con i18n
 * - permitir una imagen principal responsive
 */
const useStyles = makeStyles((theme) => ({
  /**
   * Wrapper general de la pantalla.
   * Se mantiene el mismo enfoque visual que About:
   * ancho completo, maxWidth controlado y padding responsive.
   */
  wrapper: {
    width: "100%",
    maxWidth: 980,
    margin: "0 auto",
    padding: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },

  /**
   * Título principal de la card.
   */
  cardTitle: {
    fontWeight: 500,
    fontSize: "1.5rem",

    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
    },
  },

  /**
   * Cuerpo de la tarjeta.
   * Se centra el contenido y se limita el ancho de lectura.
   */
  cardBody: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2.5),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },

  /**
   * Texto introductorio principal.
   */
  intro: {
    width: "100%",
    maxWidth: 820,
    textAlign: "center",
    lineHeight: 1.75,
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(0, 1),
    whiteSpace: "pre-line",

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.95rem",
      lineHeight: 1.65,
      padding: theme.spacing(0, 0.5),
    },
  },

  /**
   * Imagen principal de la guía.
   * Responsive y visualmente consistente con About.
   */
  image: {
    display: "block",
    width: "100%",
    maxWidth: 720,
    height: "auto",
    margin: "0 auto",
    borderRadius: 6,
    objectFit: "cover",
  },

  /**
   * Caja destacada para la guía rápida.
   */
  quickStart: {
    width: "100%",
    maxWidth: 820,
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 8,
    backgroundColor: theme.palette.grey[100],

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },

  /**
   * Lista de pasos.
   */
  stepList: {
    margin: theme.spacing(1.5, 0, 0, 0),
    paddingLeft: theme.spacing(3),

    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(2.5),
    },
  },

  /**
   * Cada paso individual.
   */
  stepItem: {
    marginBottom: theme.spacing(2),

    "&:last-child": {
      marginBottom: 0,
    },
  },

  /**
   * Título de sección o subtítulo.
   */
  sectionTitle: {
    width: "100%",
    maxWidth: 820,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },

  /**
   * Cuerpo de texto de cada sección.
   * whiteSpace: pre-line permite meter saltos de línea en los JSON.
   */
  sectionBody: {
    width: "100%",
    maxWidth: 820,
    lineHeight: 1.75,
    color: theme.palette.text.primary,
    whiteSpace: "pre-line",

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.95rem",
      lineHeight: 1.65,
    },
  },

  /**
   * Separador visual entre bloques.
   */
  divider: {
    width: "100%",
    maxWidth: 820,
    margin: theme.spacing(3, 0),
  },

  /**
   * Bloque de cada sección informativa.
   */
  section: {
    width: "100%",
    maxWidth: 820,
  },

  /**
   * Texto de versión al pie.
   */
  version: {
    marginTop: theme.spacing(1.5),
    textAlign: "center",
    fontSize: "0.9rem",
    opacity: 0.75,
  },
}));

export default function Guide() {
  const classes = useStyles();
  const { t } = useTranslation("userGuide");

  /**
   * Pasos rápidos de uso.
   * Se mantienen separados del resto para destacar mejor el flujo principal.
   */
  const quickSteps = [
    {
      title: t("step1Title", {
        defaultValue: "Accede a la búsqueda de aplicaciones",
      }),
      body: t("step1Body", {
        defaultValue:
          "Utiliza la sección de búsqueda para localizar la aplicación que deseas consultar. Puedes escribir su nombre y seleccionar el resultado correspondiente si está disponible en el sistema.",
      }),
    },
    {
      title: t("step2Title", {
        defaultValue: "Consulta el análisis de privacidad disponible",
      }),
      body: t("step2Body", {
        defaultValue:
          "Una vez localizada la aplicación, revisa la información mostrada por ePalSafer. La plataforma presenta indicadores, métricas y elementos de apoyo para ayudarte a comprender mejor el posible nivel de riesgo de privacidad asociado a la app analizada.",
      }),
    },
    {
      title: t("step3Title", {
        defaultValue: "Interpreta los resultados con criterio",
      }),
      body: t("step3Body", {
        defaultValue:
          "Los resultados deben entenderse como una ayuda para la toma de decisiones. No sustituyen una auditoría legal, técnica o forense completa, pero sí proporcionan una referencia clara para identificar señales de riesgo y comparar aplicaciones.",
      }),
    },
    {
      title: t("step4Title", {
        defaultValue: "Ajusta idioma y revisa la información legal",
      }),
      body: t("step4Body", {
        defaultValue:
          "Desde la aplicación puedes consultar la política de privacidad, la información sobre el proyecto y modificar la configuración disponible, incluido el idioma cuando corresponda.",
      }),
    },
  ];

  /**
   * Secciones largas de explicación.
   * Están pensadas para cubrir el uso general completo de la plataforma.
   */
  const sections = [
    {
      title: t("section1Title", {
        defaultValue: "¿Qué hace ePalSafer?",
      }),
      body: t("section1Body", {
        defaultValue:
          "ePalSafer es una aplicación orientada a ayudar a las personas usuarias a consultar y comprender análisis relacionados con privacidad en aplicaciones móviles y servicios digitales.\n\nLa plataforma centraliza resultados ya procesados y los presenta de forma más clara, accesible y utilizable, para que puedas conocer mejor el comportamiento general de una aplicación antes de tomar decisiones sobre su uso.",
      }),
    },
    {
      title: t("section2Title", {
        defaultValue: "¿Cómo se utiliza la aplicación?",
      }),
      body: t("section2Body", {
        defaultValue:
          "El flujo principal de uso comienza en la sección de búsqueda. Desde ahí puedes localizar la aplicación que te interesa y acceder a sus resultados.\n\nUna vez dentro, la interfaz muestra información estructurada para facilitar la consulta. La idea no es que tengas que interpretar datos técnicos complejos por tu cuenta, sino ofrecer una vista comprensible y ordenada del análisis disponible.",
      }),
    },
    {
      title: t("section3Title", {
        defaultValue: "¿Cómo interpretar los resultados?",
      }),
      body: t("section3Body", {
        defaultValue:
          "Los resultados deben leerse como indicadores de apoyo. Sirven para detectar señales relevantes, comparar aplicaciones y entender mejor determinados riesgos o patrones de comportamiento relacionados con la privacidad.\n\nEs importante no interpretar la información como una garantía absoluta ni como una certificación formal. El objetivo es aportar contexto útil y mejorar la capacidad de evaluación de la persona usuaria.",
      }),
    },
    {
      title: t("section4Title", {
        defaultValue: "Privacidad y tratamiento de datos",
      }),
      body: t("section4Body", {
        defaultValue:
          "La aplicación incluye información legal y de privacidad para que puedas entender cómo se gestiona el servicio y qué limitaciones tiene. También incorpora un banner de cookies y accesos directos a documentación complementaria cuando corresponda.\n\nTe recomendamos consultar la política de privacidad para conocer con más detalle el tratamiento de datos aplicable a este entorno.",
      }),
    },
    {
      title: t("section5Title", {
        defaultValue: "Idioma, configuración y navegación",
      }),
      body: t("section5Body", {
        defaultValue:
          "Desde la barra lateral puedes navegar por las distintas secciones disponibles de la aplicación, incluyendo búsqueda, guía de usuario, política de privacidad, sobre nosotros y configuración.\n\nLa interfaz está preparada para internacionalización, por lo que el contenido puede mostrarse en distintos idiomas según la configuración disponible.",
      }),
    },
    {
      title: t("section6Title", {
        defaultValue: "Recomendaciones generales de uso",
      }),
      body: t("section6Body", {
        defaultValue:
          "Para obtener una mejor experiencia, utiliza la aplicación como una herramienta de consulta y apoyo a la decisión. Revisa los resultados con calma, compáralos con tus necesidades y ten presente que el contexto de uso de cada app también importa.\n\nSi una aplicación no aparece o no dispone todavía de análisis visible, eso no implica automáticamente ausencia de riesgo; solo indica que no hay información consultable en ese momento dentro del sistema.",
      }),
    },
  ];

  return (
    <GridContainer justify="center" className={classes.wrapper}>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary">
            <Typography variant="h5" className={classes.cardTitle}>
              {t("title", { defaultValue: "Guía de Usuario" })}
            </Typography>
          </CardHeader>

          <CardBody className={classes.cardBody}>
            <Typography variant="body1" className={classes.intro}>
              {t("intro", {
                defaultValue:
                  "Bienvenido/a a ePalSafer. Esta guía de usuario explica qué hace la aplicación, cuál es su finalidad y cómo utilizar sus distintas secciones de forma correcta.\n\nEl objetivo es que cualquier persona pueda entender rápidamente cómo navegar por la plataforma, cómo consultar información sobre aplicaciones y cómo interpretar los resultados mostrados.",
              })}
            </Typography>

            <img
              src={GuideImage}
              alt={t("image-alt", {
                defaultValue: "Guía de usuario de ePalSafer",
              })}
              className={classes.image}
            />

            <div className={classes.quickStart}>
              <Typography variant="h6" className={classes.sectionTitle}>
                {t("quickStartTitle", {
                  defaultValue: "Guía rápida de uso",
                })}
              </Typography>

              <ol className={classes.stepList}>
                {quickSteps.map((step, index) => (
                  <li key={`quick-step-${index}`} className={classes.stepItem}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" className={classes.sectionBody}>
                      {step.body}
                    </Typography>
                  </li>
                ))}
              </ol>
            </div>

            <Divider className={classes.divider} />

            {sections.map((section, index) => (
              <React.Fragment key={`guide-section-${index}`}>
                <section className={classes.section}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {section.title}
                  </Typography>

                  <Typography variant="body1" className={classes.sectionBody}>
                    {section.body}
                  </Typography>
                </section>

                {index < sections.length - 1 && (
                  <Divider className={classes.divider} />
                )}
              </React.Fragment>
            ))}
          </CardBody>
        </Card>

        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.version}
        >
          1.0.5-release
        </Typography>
      </GridItem>
    </GridContainer>
  );
}